import React from "react"
import { inject, observer } from "mobx-react"
import styled from "styled-components"

import FormError from "~/components/Form/ErrorText"
import withTranslations from "~/components/_hocs/withTranslations"
import EmbeddedStateDropdown from "~/components/traffic/lookup/Forms/Elements/EmbeddedStateDropdown"
import constants from "~/style/constants"

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.$labelGap ?? 10}px;

  /* Match Spacer desktop={16} mobile={4} used by PlateNumber, LicenseState, DOB */
  ${(p) => p.$labelGap && p.theme && p.theme.media && p.theme.media.smallDesktop`
    gap: 4px;
  `}
`

const LabelRow = styled.div`
  font-family: ${(p) => p.theme.fonts.avenirNextMedium};
  font-size: 0.9375rem;
  height: 21px;
  color: #374553;
  display: flex;
  align-items: center;
  gap: 8px;
`

/** Single-line combo: license input + state selector (2-letter display, full names in dropdown) */
const ComboRow = styled.div`
  display: flex;
  align-items: stretch;
  height: 70px;
  width: 100%;
  border: 1px solid
    ${(p) => (p.$error ? p.theme.colors.error : constants.colors.ui.borderGray)};
  border-radius: 4px;
  box-shadow: 0px 8px 15px 0px rgba(35, 41, 48, 0.15);
  background: #fff;

  @media (max-width: 768px) {
    height: 56px;
    box-shadow: 0 2px 8px rgba(35, 41, 48, 0.06);
  }

  @media (max-width: 480px) {
    height: 48px;
  }
`

const ComboInput = styled.input`
  flex: 1;
  min-width: 200px;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-appearance: none;
  border: none;
  outline: none;
  border-radius: 4px 0 0 4px;
  padding: 0 16px;
  font-family: ${(p) => p.theme.fonts.avenirNextRegular};
  font-size: 1rem;
  color: ${(p) => p.theme.colors.text.input};
  background: transparent;
  caret-color: ${(p) => p.theme.colors.primary};

  &::placeholder {
    color: ${(p) => p.theme.colors.text.inputPlaceholder};
  }

  @media (max-width: 768px) {
    min-width: 160px;
    font-size: 0.9rem;
    padding: 0 14px;
  }

  @media (max-width: 480px) {
    min-width: 120px;
    font-size: 0.85rem;
    padding: 0 12px;
  }
`

const StateSlot = styled.div`
  width: 72px;
  flex-shrink: 0;
  border-left: 1px solid ${constants.colors.ui.borderGray};
  border-radius: 0 4px 4px 0;
  display: flex;
  align-items: stretch;
  min-height: 0;
  position: relative;
  z-index: 1;
  overflow: hidden;

  & > div {
    width: 100%;
    min-width: 0;
  }

  /* Embed dropdown without its own border; match combo height; center 2-letter state code */
  & .react-select__control {
    border: none !important;
    border-radius: 0 4px 4px 0 !important;
    box-shadow: none !important;
    min-height: 100% !important;
    height: 70px !important;
    cursor: pointer !important;
  }
  & .react-select__value-container {
    padding: 0 0 0 14px !important;
    margin-left: 0 !important;
    margin-top: 0 !important;
    justify-content: center !important;
    text-align: center !important;
  }
  & .react-select__single-value {
    margin: 0 !important;
    text-align: center !important;
    width: 100%;
  }
  & .react-select__input-container {
    margin: 0 !important;
    padding: 0 !important;
  }
  & .react-select__indicator {
    padding: 0 4px 0 0 !important;
  }

  @media (max-width: 768px) {
    width: 68px;
    & .react-select__control {
      height: 56px !important;
    }
    & .react-select__value-container {
      padding: 0 0 0 10px !important;
    }
    & .react-select__indicator {
      padding-right: 4px !important;
    }
  }

  @media (max-width: 480px) {
    width: 64px;
    & .react-select__control {
      height: 48px !important;
    }
    & .react-select__value-container {
      padding: 0 0 0 8px !important;
    }
    & .react-select__indicator {
      padding-right: 4px !important;
    }
  }
`

@inject((all) => ({
  validateDisplayMessage: all.store.trafficLookup.validateDisplayMessage,
  licenseIDNumber: all.store.trafficLookup.licenseID,
  changeLicenseIDNumber: all.store.trafficLookup.changeLicenseIDNumber,
  licenseState: all.store.trafficLookup.licenseState,
  setLicenseState: all.store.trafficLookup.setLicenseState,
  fetcherName: all.store.trafficLookup.fetcherName,
}))
@observer
class LicenseWithState extends React.Component {
  handleLicenseChange = (e) => {
    this.props.changeLicenseIDNumber(e.target.value)
  }

  render() {
    const {
      translate,
      licenseIDNumber,
      licenseState,
      setLicenseState,
      validateDisplayMessage,
      fetcherName,
    } = this.props
    const licenseError = validateDisplayMessage("licenseID")
    const isNY = fetcherName === "NY"
    const licensePlaceholder =  translate("license.placeholder")
    const licenseMaxLength = 20 //isNY ? 9 : 20
    // because user might enter license before switching to NY the state

    const labelGap = this.props.labelGap
    return (
      <Wrap $labelGap={labelGap}>
        <LabelRow>
          {translate("license.label")} / {translate("licenseState.label")}
        </LabelRow>
        <ComboRow $error={!!licenseError}>
          <ComboInput
            type={isNY ? "tel" : "text"}
            placeholder={licensePlaceholder}
            value={licenseIDNumber}
            onChange={this.handleLicenseChange}
            maxLength={licenseMaxLength}
            aria-invalid={!!licenseError}
          />
          <StateSlot>
            <EmbeddedStateDropdown
              selectedState={licenseState?.shortName ?? "NY"}
              onChange={setLicenseState}
            />
          </StateSlot>
        </ComboRow>
        <FormError error={licenseError} />
      </Wrap>
    )
  }
}

export default withTranslations(LicenseWithState, "form.traffic")
